import React, { useState, useEffect } from 'react';
import util from '../../utils/util';
import { useHistory } from 'react-router-dom';
import {
    Table, Button, Modal, Form, Input, Select, DatePicker,
    Tag, Space, Tooltip, Popconfirm, message, Card, Row, Col,
    Statistic, Badge, Drawer, Switch, Divider
} from 'antd';
import {
    PlusOutlined, EditOutlined, DeleteOutlined, EyeOutlined,
    SettingOutlined, CopyOutlined, FileTextOutlined,
    BarChartOutlined, TeamOutlined, CheckCircleOutlined
} from '@ant-design/icons';
import MeService from '../../services/MeService';
import moment from 'moment';

const { Option }   = Select;
const { TextArea } = Input;

// ── Constants ──────────────────────────────────────────────────────────────
const FRAMEWORK_TYPES = [
    { value:'standard',  label:'Standard',          icon:'📋', color:'#1890ff', desc:'ISO, SANS, WHO, clinical standards' },
    { value:'workplan',  label:'Workplan',           icon:'📅', color:'#52c41a', desc:'Annual / quarterly workplans' },
    { value:'strategy',  label:'Strategy',           icon:'🎯', color:'#722ed1', desc:'National / organisational strategies' },
    { value:'sdg',       label:'SDG / MDG',          icon:'🌍', color:'#13c2c2', desc:'Sustainable Development Goals' },
    { value:'policy',    label:'Policy',             icon:'⚖️',  color:'#fa8c16', desc:'Policy implementation plans' },
    { value:'programme', label:'Programme',          icon:'🏥', color:'#eb2f96', desc:'Programme implementation plans' },
    { value:'other',     label:'Other',              icon:'📌', color:'#8c8c8c', desc:'Any other M&E framework' },
];

const SCOPES = [
    { value:'national',   label:'National'   },
    { value:'regional',   label:'Regional'   },
    { value:'facility',   label:'Facility'   },
    { value:'department', label:'Department' },
];

const STATUS_COLORS = { draft:'default', active:'success', archived:'warning' };

const getType = (val) => FRAMEWORK_TYPES.find(t => t.value === val) || FRAMEWORK_TYPES[0];

// ── Framework Form ─────────────────────────────────────────────────────────
function FrameworkForm({ visible, onClose, onSaved, editRecord }) {
    const [form]    = Form.useForm();
    const [saving,  setSaving]  = useState(false);

    useEffect(() => {
        if (visible) {
            if (editRecord) {
                form.setFieldsValue({
                    ...editRecord,
                    effective_date: editRecord.effective_date ? moment(editRecord.effective_date) : null,
                    end_date:       editRecord.end_date       ? moment(editRecord.end_date)       : null,
                    applicable_types: editRecord.applicable_types
                        ? (Array.isArray(editRecord.applicable_types)
                            ? editRecord.applicable_types
                            : JSON.parse(editRecord.applicable_types || '[]'))
                        : [],
                });
            } else {
                form.resetFields();
                form.setFieldsValue({ status:'draft', scope:'facility', allow_self_report:true, require_evidence:true });
            }
        }
    }, [visible, editRecord]); // eslint-disable-line

    const onFinish = async (values) => {
        setSaving(true);
        try {
            const payload = {
                ...values,
                id:             editRecord?.id || null,
                effective_date: values.effective_date ? values.effective_date.format('YYYY-MM-DD') : null,
                end_date:       values.end_date       ? values.end_date.format('YYYY-MM-DD')       : null,
                allow_self_report: values.allow_self_report ? 1 : 0,
                require_evidence:  values.require_evidence  ? 1 : 0,
            };
            const r = await MeService.saveFramework(payload);
            if (r.data?.code !== 200) throw new Error(r.data?.message);
            message.success(editRecord ? 'Framework updated' : 'Framework created');
            onSaved();
            onClose();
        } catch(e) { message.error(e.message || 'Save failed'); }
        finally { setSaving(false); }
    };

    return (
        <Drawer
            title={
                <span style={{ fontWeight:800, color:'#1f3864' }}>
                    {editRecord ? '✏️ Edit Framework' : '➕ New Framework'}
                </span>
            }
            open={visible} onClose={onClose}
            width={600} destroyOnClose
            footer={
                <div style={{ display:'flex', gap:8, justifyContent:'flex-end' }}>
                    <Button onClick={onClose}>Cancel</Button>
                    <Button type="primary" loading={saving} onClick={() => form.submit()}
                        style={{ background:'#1f3864', borderColor:'#1f3864' }}>
                        {editRecord ? 'Update' : 'Create Framework'}
                    </Button>
                </div>
            }
        >
            <Form form={form} layout="vertical" onFinish={onFinish}>
                <Row gutter={12}>
                    <Col span={16}>
                        <Form.Item name="name" label="Framework Name" rules={[{ required:true }]}>
                            <Input placeholder="e.g. ISO 9001:2015 Quality Management"/>
                        </Form.Item>
                    </Col>
                    <Col span={8}>
                        <Form.Item name="code" label="Short Code" rules={[{ required:true }]}>
                            <Input placeholder="ISO9001" style={{ textTransform:'uppercase' }}/>
                        </Form.Item>
                    </Col>
                </Row>

                <Row gutter={12}>
                    <Col span={12}>
                        <Form.Item name="type" label="Framework Type" rules={[{ required:true }]}>
                            <Select placeholder="Select type">
                                {FRAMEWORK_TYPES.map(t => (
                                    <Option key={t.value} value={t.value}>
                                        {t.icon} {t.label}
                                        <span style={{ fontSize:11, color:'#888', marginLeft:6 }}>— {t.desc}</span>
                                    </Option>
                                ))}
                            </Select>
                        </Form.Item>
                    </Col>
                    <Col span={12}>
                        <Form.Item name="issuing_body" label="Issuing Body">
                            <Input placeholder="e.g. ISO, MoH, UN, Cabinet"/>
                        </Form.Item>
                    </Col>
                </Row>

                <Row gutter={12}>
                    <Col span={8}>
                        <Form.Item name="version" label="Version / Edition">
                            <Input placeholder="2015, FY2025/26"/>
                        </Form.Item>
                    </Col>
                    <Col span={8}>
                        <Form.Item name="effective_date" label="Effective Date">
                            <DatePicker style={{ width:'100%' }}/>
                        </Form.Item>
                    </Col>
                    <Col span={8}>
                        <Form.Item name="end_date" label="End Date">
                            <DatePicker style={{ width:'100%' }}/>
                        </Form.Item>
                    </Col>
                </Row>

                <Row gutter={12}>
                    <Col span={12}>
                        <Form.Item name="scope" label="Scope">
                            <Select>
                                {SCOPES.map(s => <Option key={s.value} value={s.value}>{s.label}</Option>)}
                            </Select>
                        </Form.Item>
                    </Col>
                    <Col span={12}>
                        <Form.Item name="applicable_types" label="Applicable Facility Types">
                            <Select mode="multiple" placeholder="All types">
                                <Option value="hospital">Hospital</Option>
                                <Option value="health centre">Health Centre</Option>
                                <Option value="clinic">Clinic / PHU</Option>
                                <Option value="laboratory">Laboratory</Option>
                                <Option value="pharmacy">Pharmacy</Option>
                            </Select>
                        </Form.Item>
                    </Col>
                </Row>

                <Form.Item name="description" label="Description">
                    <TextArea rows={3} placeholder="What this framework covers and its objectives..."/>
                </Form.Item>

                <Row gutter={12}>
                    <Col span={8}>
                        <Form.Item name="status" label="Status">
                            <Select>
                                <Option value="draft">Draft</Option>
                                <Option value="active">Active</Option>
                                <Option value="archived">Archived</Option>
                            </Select>
                        </Form.Item>
                    </Col>
                    <Col span={8}>
                        <Form.Item name="allow_self_report" label="Allow Self-Reporting" valuePropName="checked">
                            <Switch checkedChildren="Yes" unCheckedChildren="No"/>
                        </Form.Item>
                    </Col>
                    <Col span={8}>
                        <Form.Item name="require_evidence" label="Require Evidence" valuePropName="checked">
                            <Switch checkedChildren="Yes" unCheckedChildren="No"/>
                        </Form.Item>
                    </Col>
                </Row>
            </Form>
        </Drawer>
    );
}

// ── Framework Card ─────────────────────────────────────────────────────────
function FrameworkCard({ fw, onEdit, onDelete, onView, onSetup }) {
    const type = getType(fw.type);
    return (
        <Card
            size="small"
            style={{
                borderRadius:10,
                border:`2px solid ${type.color}22`,
                borderTop:`4px solid ${type.color}`,
                height:'100%',
            }}
            bodyStyle={{ padding:16 }}
        >
            {/* Header */}
            <div style={{ display:'flex', alignItems:'flex-start', gap:10, marginBottom:10 }}>
                <div style={{ fontSize:28, flexShrink:0 }}>{type.icon}</div>
                <div style={{ flex:1, minWidth:0 }}>
                    <div style={{ fontWeight:800, fontSize:14, color:'#1f3864',
                        overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
                        {fw.name}
                    </div>
                    <div style={{ fontSize:11, color:'#888', marginTop:2 }}>
                        <Tag color={type.color} style={{ fontSize:10 }}>{fw.code}</Tag>
                        {fw.version && <span style={{ marginLeft:4 }}>v{fw.version}</span>}
                        {fw.issuing_body && <span style={{ marginLeft:4 }}>· {fw.issuing_body}</span>}
                    </div>
                </div>
                <Badge status={STATUS_COLORS[fw.status]} text={fw.status} style={{ fontSize:11 }}/>
            </div>

            {/* Description */}
            {fw.description && (
                <div style={{ fontSize:12, color:'#555', marginBottom:10,
                    display:'-webkit-box', WebkitLineClamp:2, WebkitBoxOrient:'vertical',
                    overflow:'hidden' }}>
                    {fw.description}
                </div>
            )}

            {/* Stats */}
            <div style={{ display:'flex', gap:8, marginBottom:12 }}>
                <div style={{ flex:1, background:'#f8faff', borderRadius:6, padding:'6px 8px', textAlign:'center' }}>
                    <div style={{ fontSize:18, fontWeight:800, color:'#1890ff' }}>{fw.section_count || 0}</div>
                    <div style={{ fontSize:10, color:'#888' }}>Sections</div>
                </div>
                <div style={{ flex:1, background:'#f8faff', borderRadius:6, padding:'6px 8px', textAlign:'center' }}>
                    <div style={{ fontSize:18, fontWeight:800, color:'#7c3aed' }}>{fw.indicator_count || 0}</div>
                    <div style={{ fontSize:10, color:'#888' }}>Indicators</div>
                </div>
                <div style={{ flex:1, background:'#f8faff', borderRadius:6, padding:'6px 8px', textAlign:'center' }}>
                    <div style={{ fontSize:18, fontWeight:800, color:'#52c41a' }}>{fw.assignment_count || 0}</div>
                    <div style={{ fontSize:10, color:'#888' }}>Assigned</div>
                </div>
            </div>

            {/* Dates */}
            {(fw.effective_date || fw.end_date) && (
                <div style={{ fontSize:11, color:'#888', marginBottom:10 }}>
                    📅 {fw.effective_date ? moment(fw.effective_date).format('MMM YYYY') : '—'}
                    {fw.end_date && ` → ${moment(fw.end_date).format('MMM YYYY')}`}
                </div>
            )}

            {/* Actions */}
            <Divider style={{ margin:'8px 0' }}/>
            <div style={{ display:'flex', gap:6, flexWrap:'wrap' }}>
                <Tooltip title="View Details">
                    <Button size="small" icon={<EyeOutlined/>} onClick={() => onView(fw)}>View</Button>
                </Tooltip>
                <Tooltip title="Build Structure (Sections + Indicators)">
                    <Button size="small" icon={<SettingOutlined/>} type="primary"
                        style={{ background:'#7c3aed', borderColor:'#7c3aed' }}
                        onClick={() => onSetup(fw)}>
                        Structure
                    </Button>
                </Tooltip>
                <Tooltip title="Edit">
                    <Button size="small" icon={<EditOutlined/>} onClick={() => onEdit(fw)}/>
                </Tooltip>
                <Tooltip title="Delete">
                    <Popconfirm
                        title="Delete this framework?"
                        description="Cannot delete if assignments exist."
                        onConfirm={() => onDelete(fw.id)}
                        okText="Delete" okButtonProps={{ danger:true }}
                    >
                        <Button size="small" icon={<DeleteOutlined/>} danger/>
                    </Popconfirm>
                </Tooltip>
            </div>
        </Card>
    );
}

// ── Framework Detail Drawer (quick view) ───────────────────────────────────
function FrameworkDetailDrawer({ visible, onClose, framework }) {
    if (!framework) return null;
    const type = getType(framework.type);
    return (
        <Drawer
            title={<span style={{ fontWeight:800 }}>{type.icon} {framework.name}</span>}
            open={visible} onClose={onClose} width={480}
        >
            <div style={{ display:'flex', gap:8, flexWrap:'wrap', marginBottom:16 }}>
                <Tag color={type.color}>{type.label}</Tag>
                <Tag color={STATUS_COLORS[framework.status]}>{framework.status}</Tag>
                {framework.scope && <Tag>{framework.scope}</Tag>}
            </div>

            {[
                { label:'Code',         value: framework.code },
                { label:'Issuing Body', value: framework.issuing_body },
                { label:'Version',      value: framework.version },
                { label:'Effective',    value: framework.effective_date ? moment(framework.effective_date).format('DD MMM YYYY') : null },
                { label:'End Date',     value: framework.end_date ? moment(framework.end_date).format('DD MMM YYYY') : null },
                { label:'Created By',   value: framework.created_by_name },
                { label:'Self-Report',  value: framework.allow_self_report ? 'Allowed' : 'Not allowed' },
                { label:'Evidence',     value: framework.require_evidence  ? 'Required' : 'Optional' },
            ].filter(r => r.value).map(r => (
                <div key={r.label} style={{ display:'flex', gap:8, padding:'8px 0',
                    borderBottom:'1px solid #f0f0f0', fontSize:13 }}>
                    <span style={{ color:'#888', width:100, flexShrink:0 }}>{r.label}</span>
                    <span style={{ fontWeight:600 }}>{r.value}</span>
                </div>
            ))}

            {framework.description && (
                <div style={{ marginTop:16, padding:12, background:'#f8faff', borderRadius:8 }}>
                    <div style={{ fontSize:11, color:'#888', marginBottom:4 }}>Description</div>
                    <div style={{ fontSize:13, lineHeight:1.6 }}>{framework.description}</div>
                </div>
            )}

            <div style={{ display:'flex', gap:10, marginTop:20 }}>
                <div style={{ flex:1, textAlign:'center', padding:12, background:'#f0f5ff', borderRadius:8 }}>
                    <div style={{ fontSize:24, fontWeight:800, color:'#1890ff' }}>{framework.section_count || 0}</div>
                    <div style={{ fontSize:12, color:'#888' }}>Sections</div>
                </div>
                <div style={{ flex:1, textAlign:'center', padding:12, background:'#f9f0ff', borderRadius:8 }}>
                    <div style={{ fontSize:24, fontWeight:800, color:'#7c3aed' }}>{framework.indicator_count || 0}</div>
                    <div style={{ fontSize:12, color:'#888' }}>Indicators</div>
                </div>
                <div style={{ flex:1, textAlign:'center', padding:12, background:'#f6ffed', borderRadius:8 }}>
                    <div style={{ fontSize:24, fontWeight:800, color:'#52c41a' }}>{framework.assignment_count || 0}</div>
                    <div style={{ fontSize:12, color:'#888' }}>Assigned</div>
                </div>
            </div>
        </Drawer>
    );
}

// ── Main Page ──────────────────────────────────────────────────────────────
export default function MeFrameworks() {
    const history = useHistory();
    const [frameworks,  setFrameworks]  = useState([]);
    // Permission checks
    const canAdd    = util.canAdd('me_admin');
    const canEdit   = util.canEdit('me_admin');
    const canDelete = util.canDelete('me_admin');
    const canExport = util.canExport('me_admin');
    const [loading,     setLoading]     = useState(true);
    const [formVisible, setFormVisible] = useState(false);
    const [editRecord,  setEditRecord]  = useState(null);
    const [viewRecord,  setViewRecord]  = useState(null);
    const [filterType,  setFilterType]  = useState('');
    const [filterStatus,setFilterStatus]= useState('active');
    const [search,      setSearch]      = useState('');
    const [viewMode,    setViewMode]    = useState('cards'); // cards | table

    const load = async () => {
        setLoading(true);
        try {
            const r = await MeService.getFrameworks({
                ...(filterType   ? { type:filterType }     : {}),
                ...(filterStatus ? { status:filterStatus } : {}),
            });
            setFrameworks(r.data?.data || []);
        } catch(e) { message.error('Failed to load frameworks'); }
        finally { setLoading(false); }
    };

    useEffect(() => { load(); }, [filterType, filterStatus]); // eslint-disable-line

    const handleEdit   = (fw)  => { setEditRecord(fw); setFormVisible(true); };
    const handleView   = (fw)  => setViewRecord(fw);
    const handleSetup  = (fw)  => history.push(`/me/frameworks/${fw.id}/structure`);

    const handleDelete = async (id) => {
        try {
            const r = await MeService.deleteFramework(id);
            if (r.data?.code !== 200) throw new Error(r.data?.message);
            message.success('Framework deleted');
            load();
        } catch(e) { message.error(e.message); }
    };

    // Stats
    const total    = frameworks.length;
    const active   = frameworks.filter(f => f.status === 'active').length;
    const assigned = frameworks.reduce((s, f) => s + parseInt(f.assignment_count || 0), 0);
    const byType   = FRAMEWORK_TYPES.map(t => ({
        ...t, count: frameworks.filter(f => f.type === t.value).length
    })).filter(t => t.count > 0);

    const filtered = frameworks.filter(fw =>
        !search || fw.name.toLowerCase().includes(search.toLowerCase()) ||
        fw.code.toLowerCase().includes(search.toLowerCase())
    );

    // Table columns
    const columns = [
        { title:'', width:32, render:(_, fw) => <span style={{ fontSize:18 }}>{getType(fw.type).icon}</span> },
        { title:'Framework', dataIndex:'name', render:(v, fw) => (
            <div>
                <div style={{ fontWeight:700, color:'#1f3864' }}>{v}</div>
                <div style={{ fontSize:11, color:'#888' }}>
                    <Tag color={getType(fw.type).color} style={{ fontSize:10 }}>{fw.code}</Tag>
                    {fw.version && `v${fw.version}`}
                    {fw.issuing_body && ` · ${fw.issuing_body}`}
                </div>
            </div>
        )},
        { title:'Type', dataIndex:'type', width:120,
            render: v => { const t = getType(v); return <Tag color={t.color}>{t.label}</Tag>; }},
        { title:'Scope', dataIndex:'scope', width:100, render: v => <Tag>{v}</Tag> },
        { title:'Sections', dataIndex:'section_count',   width:80,  align:'center', render: v => <strong>{v||0}</strong> },
        { title:'Indicators',dataIndex:'indicator_count', width:90,  align:'center', render: v => <strong style={{ color:'#7c3aed' }}>{v||0}</strong> },
        { title:'Assigned',  dataIndex:'assignment_count',width:80,  align:'center', render: v => <strong style={{ color:'#52c41a' }}>{v||0}</strong> },
        { title:'Status', dataIndex:'status', width:90,
            render: v => <Badge status={STATUS_COLORS[v]} text={v}/> },
        { title:'Actions', width:160, render:(_, fw) => (
            <Space size="small">
                <Tooltip title="View"><Button size="small" icon={<EyeOutlined/>} onClick={() => handleView(fw)}/></Tooltip>
                <Tooltip title="Structure"><Button size="small" icon={<SettingOutlined/>}
                    style={{ color:'#7c3aed', borderColor:'#7c3aed' }} onClick={() => handleSetup(fw)}/></Tooltip>
                <Tooltip title="Edit"><Button size="small" icon={<EditOutlined/>} onClick={() => handleEdit(fw)}/></Tooltip>
                <Popconfirm title="Delete?" onConfirm={() => handleDelete(fw.id)} okButtonProps={{ danger:true }}>
                    <Button size="small" icon={<DeleteOutlined/>} danger/>
                </Popconfirm>
            </Space>
        )},
    ];

    return (
        <div className="page-content">
            {/* Page header */}
            <div className="page-head d-flex">
                <div className="my-auto">
                    <h4 style={{ margin:0 }}>📋 M&E Framework Library</h4>
                    <small className="text-muted">
                        Manage ISO standards, workplans, strategies, SDGs and any M&E framework
                    </small>
                </div>
                <div className="my-auto ml-auto">
                    <Button type="primary" icon={<PlusOutlined/>}
                        style={{ background:'#1f3864', borderColor:'#1f3864' }}
                        onClick={() => { setEditRecord(null); setFormVisible(true); }}>
                        New Framework
                    </Button>
                </div>
            </div>

            <div className="page-pad">
            {/* KPI strip */}
            <Row gutter={12} style={{ marginBottom:16 }}>
                <Col xs={8} sm={6} md={4}>
                    <Card size="small" style={{ textAlign:'center', borderLeft:'4px solid #1890ff' }}>
                        <Statistic title="Total" value={total} valueStyle={{ color:'#1890ff', fontSize:22, fontWeight:800 }}/>
                    </Card>
                </Col>
                <Col xs={8} sm={6} md={4}>
                    <Card size="small" style={{ textAlign:'center', borderLeft:'4px solid #52c41a' }}>
                        <Statistic title="Active" value={active} valueStyle={{ color:'#52c41a', fontSize:22, fontWeight:800 }}/>
                    </Card>
                </Col>
                <Col xs={8} sm={6} md={4}>
                    <Card size="small" style={{ textAlign:'center', borderLeft:'4px solid #7c3aed' }}>
                        <Statistic title="Assignments" value={assigned} valueStyle={{ color:'#7c3aed', fontSize:22, fontWeight:800 }}/>
                    </Card>
                </Col>
                {byType.map(t => (
                    <Col key={t.value} xs={12} sm={6} md={4}>
                        <Card size="small" style={{ textAlign:'center', borderLeft:`4px solid ${t.color}` }}>
                            <div style={{ fontSize:18 }}>{t.icon}</div>
                            <div style={{ fontSize:18, fontWeight:800, color:t.color }}>{t.count}</div>
                            <div style={{ fontSize:11, color:'#888' }}>{t.label}</div>
                        </Card>
                    </Col>
                ))}
            </Row>

            {/* Filters */}
            <Card size="small" style={{ marginBottom:16 }}>
                <div style={{ display:'flex', gap:8, flexWrap:'wrap', alignItems:'center' }}>
                    <Input.Search
                        placeholder="Search frameworks..."
                        value={search} onChange={e => setSearch(e.target.value)}
                        style={{ width:220 }} allowClear/>
                    <Select value={filterType} onChange={setFilterType}
                        style={{ width:160 }} allowClear placeholder="All types">
                        {FRAMEWORK_TYPES.map(t => (
                            <Option key={t.value} value={t.value}>{t.icon} {t.label}</Option>
                        ))}
                    </Select>
                    <Select value={filterStatus} onChange={setFilterStatus}
                        style={{ width:130 }} allowClear placeholder="All statuses">
                        <Option value="draft">Draft</Option>
                        <Option value="active">Active</Option>
                        <Option value="archived">Archived</Option>
                    </Select>
                    <div style={{ marginLeft:'auto', display:'flex', gap:4 }}>
                        <Button
                            type={viewMode==='cards'?'primary':'default'}
                            size="small"
                            onClick={() => setViewMode('cards')}
                            style={viewMode==='cards'?{background:'#1f3864',borderColor:'#1f3864'}:{}}>
                            Cards
                        </Button>
                        <Button
                            type={viewMode==='table'?'primary':'default'}
                            size="small"
                            onClick={() => setViewMode('table')}
                            style={viewMode==='table'?{background:'#1f3864',borderColor:'#1f3864'}:{}}>
                            Table
                        </Button>
                    </div>
                </div>
            </Card>

            {/* Content */}
            {viewMode === 'cards' ? (
                loading ? (
                    <div style={{ textAlign:'center', padding:60, color:'#888' }}>Loading frameworks…</div>
                ) : filtered.length === 0 ? (
                    <Card style={{ textAlign:'center', padding:40 }}>
                        <div style={{ fontSize:40, marginBottom:12 }}>📋</div>
                        <div style={{ fontSize:16, fontWeight:600, color:'#1f3864' }}>No frameworks yet</div>
                        <div style={{ color:'#888', marginTop:4, marginBottom:16 }}>
                            Create your first framework to start monitoring implementation
                        </div>
                        <Button type="primary" icon={<PlusOutlined/>}
                            style={{ background:'#1f3864', borderColor:'#1f3864' }}
                            onClick={() => { setEditRecord(null); setFormVisible(true); }}>
                            Create Framework
                        </Button>
                    </Card>
                ) : (
                    <Row gutter={[16, 16]}>
                        {filtered.map(fw => (
                            <Col key={fw.id} xs={24} sm={12} md={8} lg={6}>
                                <FrameworkCard
                                    fw={fw}
                                    onEdit={handleEdit}
                                    onDelete={handleDelete}
                                    onView={handleView}
                                    onSetup={handleSetup}
                                />
                            </Col>
                        ))}
                    </Row>
                )
            ) : (
                <Card size="small">
                    <Table
                        size="small"
                        dataSource={filtered}
                        rowKey="id"
                        columns={columns}
                        loading={loading}
                        pagination={{ pageSize:20, showSizeChanger:true }}
                    />
                </Card>
            )}

            {/* Form drawer */}
            <FrameworkForm
                visible={formVisible}
                onClose={() => setFormVisible(false)}
                onSaved={load}
                editRecord={editRecord}
            />

            {/* Detail drawer */}
            <FrameworkDetailDrawer
                visible={!!viewRecord}
                onClose={() => setViewRecord(null)}
                framework={viewRecord}
            />
            </div>{/* end page-pad */}
        </div>
    );
}


